import { Article } from '@/types/article';

/**
 * Generate a URL-friendly slug from an article title
 * Removes special characters, converts to lowercase, replaces spaces with hyphens
 */
export function generateSlug(title: string): string {
    if (!title) return '';
    
    return title
        .toLowerCase()
        .trim()
        // Replace Cyrillic and special characters with their ASCII equivalents where possible
        .replace(/[čćžšđČĆŽŠĐ]/g, (char) => {
            const map: { [key: string]: string } = {
                'č': 'c',
                'ć': 'c',
                'ž': 'z',
                'š': 's',
                'đ': 'd',
                'Č': 'c',
                'Ć': 'c',
                'Ž': 'z',
                'Š': 's',
                'Đ': 'd',
            };
            return map[char] || char;
        })
        // Replace spaces and underscores with hyphens
        .replace(/[\s_]+/g, '-')
        // Remove any character that's not alphanumeric or hyphen
        .replace(/[^a-z0-9-]/g, '')
        // Replace multiple consecutive hyphens with a single hyphen
        .replace(/-+/g, '-')
        // Remove leading and trailing hyphens
        .replace(/^-+|-+$/g, '');
}

/**
 * Check if a string is a valid Firebase document ID (alphanumeric, no hyphens or spaces)
 */
export function isFirebaseDocId(id: string): boolean {
    return /^[a-zA-Z0-9]+$/.test(id);
}

/**
 * Check if a string is a slug (contains hyphens - NEW ARTICLE FORMAT)
 * Slugs: article-title-here  
 * Old format: ktm7Og1EidmGmAZMv3GN or numeric IDs
 */
export function isSlug(id: string): boolean {
    return id.includes('-');
}

/**
 * Generate a URL-friendly article link
 * All articles prefer slug-based URLs for better SEO.
 * New articles have a persisted `slug` field.
 * Old articles generate a slug from their title on the fly.
 * docId is kept as the ultimate fallback.
 */
export function getArticleUrl(article: Article): string {
    // 1. Persisted slug (new articles)
    if (article.slug) {
        return `/article/${article.slug}`;
    }
    // 2. Generate slug from title (old articles)
    if (article.naslov) {
        const slug = generateSlug(article.naslov);
        if (slug) return `/article/${slug}`;
    }
    // 3. Fallback to docId / numeric id (safety net)
    return `/article/${article.docId || article.id}`;
}
